import type { ReactNode } from "react";
import type { UserProfile, IssueStatus } from "@/domains/pm/types";
import { UserMultiSelect } from "@/components/ui/user-multi-select";
import { DatePicker } from "@/components/ui/date-picker";
import { StatusPicker } from "@/domains/pm/components/shared/StatusPicker";
import { useIssueDateRangeCalendar } from "@/domains/pm/components/shared/useIssueDateRangeCalendar";
import { PanelSectionLabel } from "./PanelSectionLabel";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { PANEL_DATE_FORMAT, panelDateLocale } from "@/domains/pm/utils/panelDates";
import { cn } from "@/lib/utils";

interface IssueFieldGridProps {
  orgUsers: UserProfile[];
  assigneeIds: number[];
  onAssigneesChange: (ids: number[]) => void;
  /** Listed first in the assignee picker: people responsible for the issue's disciplines. */
  suggestedAssigneeIds?: readonly number[];
  startDate: string;
  onStartDateChange: (v: string) => void;
  dueDate: string;
  onDueDateChange: (v: string) => void;
  /** Overdue is only meaningful while the issue is still open. */
  isOverdue: boolean;
  /** start_date is after due_date. Legal in the database, so it is flagged
   *  here rather than prevented — see utils/issueDates.isDateRangeInverted. */
  isDateRangeInverted: boolean;
  status: IssueStatus;
  /** The issue's project. Statuses belong to one, so the picker needs it both
   *  to list the right ones and to offer "New status". */
  buildingProjectId?: number | null;
  onStatusChange: (status: IssueStatus) => void;
  /** Read the fields without being able to change them — snapshot mode. */
  readOnly?: boolean;
  /** The dependencies field, rendered right after Assignee. Supplied by the
   *  panel, which owns the issue this grid only reports on. */
  dependencies?: ReactNode;
}

/**
 * One labelled column of the field row. `className` carries the column's floor
 * width — wide enough that its own pill/chip always fits, so a narrow panel
 * wraps the columns onto a second line instead of letting them collide
 * (WHA-3810 round 3). The column sizes to its content above that floor and
 * deliberately does NOT grow: an equal-thirds split stretched the short status
 * column and left a gap between it and the assignee beside it, so the three
 * fields now sit together with the row's gap as their only separation.
 */
function PanelField({
  label,
  className,
  children,
}: {
  label: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <div className={cn(className)}>
      <PanelSectionLabel className="mb-1.5">{label}</PanelSectionLabel>
      {children}
    </div>
  );
}

/** Start date, plus the "after the due date" warning when the pair is
 *  inverted. The warning sits on the START field rather than the due one
 *  because the start is the value that moved out of order in the common case
 *  (scheduling a start against an existing deadline). Amber, not red: it
 *  describes the data, where the red overdue pill describes the work. */
function StartDateField({
  startDate,
  dueDate,
  onStartDateChange,
  isDateRangeInverted,
  readOnly,
}: Pick<
  IssueFieldGridProps,
  "startDate" | "dueDate" | "onStartDateChange" | "isDateRangeInverted" | "readOnly"
>) {
  const { t, language } = useLanguage();
  const rangeProps = useIssueDateRangeCalendar("start", startDate, dueDate);
  return (
    <PanelField label={t.pm.issues.detail.startDate} className="min-w-[150px]">
      <div className="-ml-1.5 flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
        <DatePicker
          size="sm"
          disabled={readOnly}
          value={startDate || null}
          onChange={(v) => onStartDateChange(v ?? "")}
          placeholder={t.pm.issues.detail.setStartDate}
          displayFormat={PANEL_DATE_FORMAT}
          locale={panelDateLocale(language)}
          calendarProps={rangeProps}
          className="h-8 w-auto max-w-full border-transparent px-1.5 text-sm shadow-none hover:bg-accent"
        />
        {isDateRangeInverted && (
          <span
            role="status"
            className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
          >
            {t.pm.issues.startAfterDue}
          </span>
        )}
      </div>
    </PanelField>
  );
}

/** Due date plus its overdue chip — the chip belongs to the date, so it sits
 *  right after it and wraps under it when the column runs short. */
function DueDateField({
  startDate,
  dueDate,
  onDueDateChange,
  isOverdue,
  readOnly,
}: Pick<
  IssueFieldGridProps,
  "startDate" | "dueDate" | "onDueDateChange" | "isOverdue" | "readOnly"
>) {
  const { t, language } = useLanguage();
  const rangeProps = useIssueDateRangeCalendar("due", startDate, dueDate);
  return (
    <PanelField label={t.pm.issues.detail.dueDate} className="min-w-[150px]">
      <div className="-ml-1.5 flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
        <DatePicker
          size="sm"
          disabled={readOnly}
          value={dueDate || null}
          onChange={(v) => onDueDateChange(v ?? "")}
          // Empty state reads as an action, not a repeat of the label above.
          placeholder={t.pm.issues.detail.setDueDate}
          displayFormat={PANEL_DATE_FORMAT}
          locale={panelDateLocale(language)}
          calendarProps={rangeProps}
          className="h-8 w-auto max-w-full border-transparent px-1.5 text-sm shadow-none hover:bg-accent"
        />
        {isOverdue && (
          // WHA overdue styling: a small uppercase red OVERDUE pill following
          // the (dark) due date — the same red pill used on the list rows.
          <span className="shrink-0 rounded-full bg-[#fcebeb] px-1.5 py-0.5 text-[10px] font-medium uppercase text-[#ef4129]">
            {t.pm.issues.dateBuckets.overdue}
          </span>
        )}
      </div>
    </PanelField>
  );
}

/**
 * IssueFieldGrid — the labelled metadata row of the issue detail panel
 * (WHA-3810). Status, assignee and due date all read as labelled properties of
 * the issue; status leads the row as a pill dropdown rather than a floating
 * chip in the header. Each control carries `-ml-1.5` so its hover padding
 * doesn't push its text out of alignment with the label above it.
 */
export function IssueFieldGrid(props: IssueFieldGridProps) {
  const { orgUsers, assigneeIds, onAssigneesChange, status, buildingProjectId, onStatusChange } =
    props;
  const readOnly = props.readOnly ?? false;
  const { t } = useLanguage();
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3 py-2.5">
      {/* Status hugs its pill (min-w-0) — a wider floor here was what opened the
          gap between it and the assignee. */}
      <PanelField label={t.pm.issues.detail.status} className="min-w-0">
        <StatusPicker
          value={status}
          buildingProjectId={buildingProjectId}
          onChange={onStatusChange}
          disabled={readOnly}
          badgeVariant="pill"
          triggerClassName="-ml-1.5 h-8 rounded-md px-1.5 hover:bg-accent"
        />
      </PanelField>
      <PanelField label={t.pm.issues.detail.assignee} className="min-w-[150px]">
        <UserMultiSelect
          users={orgUsers}
          selectedUserIds={assigneeIds}
          onChange={onAssigneesChange}
          placeholder={t.pm.issues.noAssignee}
          searchPlaceholder={t.pm.issues.searchUsers}
          emptyMessage={t.pm.issues.noUsersFound}
          disabled={readOnly}
          suggestedUserIds={props.suggestedAssigneeIds}
          suggestedLabel={t.pm.issues.suggestedAssignees}
          othersLabel={t.pm.issues.otherAssignees}
          showNames
          // h-auto/min-h-8: with several assignees the trigger wraps onto a
          // second line rather than clipping them.
          className="-ml-1.5 h-auto min-h-8 w-[calc(100%+0.375rem)] justify-start gap-2 border-transparent px-1.5 py-1 text-sm shadow-none hover:bg-accent"
        />
      </PanelField>
      {props.dependencies && (
        <PanelField label={t.pm.issues.dependencies.title} className="min-w-0">
          {props.dependencies}
        </PanelField>
      )}
      <StartDateField
        startDate={props.startDate}
        dueDate={props.dueDate}
        onStartDateChange={props.onStartDateChange}
        isDateRangeInverted={props.isDateRangeInverted}
        readOnly={readOnly}
      />
      <DueDateField
        startDate={props.startDate}
        dueDate={props.dueDate}
        onDueDateChange={props.onDueDateChange}
        isOverdue={props.isOverdue}
        readOnly={readOnly}
      />
    </div>
  );
}
